import { Badge } from '../ui/badge';
import { Pencil } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

export const RISK_LEVELS = [
  { key: 'kritiek', label: 'Kritiek', color: '#B91C1C', bg: 'bg-[#B91C1C]', text: 'text-white', badgeCls: 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]', dotCls: 'bg-[#B91C1C]' },
  { key: 'hoog', label: 'Hoog', color: '#92400E', bg: 'bg-[#D97706]', text: 'text-white', badgeCls: 'bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]', dotCls: 'bg-[#D97706]' },
  { key: 'midden', label: 'Midden', color: '#92400E', bg: 'bg-[#D97706]', text: 'text-white', badgeCls: 'bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]', dotCls: 'bg-[#D97706]' },
  { key: 'laag', label: 'Laag', color: '#1D4ED8', bg: 'bg-[#1D4ED8]', text: 'text-white', badgeCls: 'bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]', dotCls: 'bg-[#1D4ED8]' },
  { key: 'miniem', label: 'Miniem', color: '#4B5563', bg: 'bg-[#6B7280]', text: 'text-white', badgeCls: 'bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB]', dotCls: 'bg-[#6B7280]' },
];

export const CONTROL_STATUSES = ['Gedefinieerd', 'Midden', 'Voldoende', 'Hoog'];
export const ACTION_STATUSES = ['Open', 'Gestart', 'Afgerond'];
export const ACTION_PRIORITIES = ['Laag', 'Midden', 'Hoog'];

export const getLevelConfig = (level) => RISK_LEVELS.find(l => l.key === level) || RISK_LEVELS[3];

export const getHeatmapColor = (score) => {
  if (score >= 20) return '#991b1b';
  if (score >= 15) return '#dc2626';
  if (score >= 10) return '#f59e0b';
  if (score >= 5) return '#22c55e';
  return '#16a34a';
};

export const getHeatmapBg = (score) => {
  if (score >= 20) return 'bg-red-900/20';
  if (score >= 15) return 'bg-red-500/15';
  if (score >= 10) return 'bg-amber-500/15';
  if (score >= 5) return 'bg-green-500/10';
  return 'bg-emerald-500/10';
};

export const RiskBadge = ({ level, size = 'sm' }) => {
  const config = getLevelConfig(level);
  return (
    <Badge className={`${config.badgeCls} text-[10px] font-semibold px-2 py-0.5 ${size === 'sm' ? '' : 'text-xs px-3 py-1'}`}
      data-testid={`risk-badge-${level}`}>
      {config.label}
    </Badge>
  );
};

export const RiskLevelIndicator = ({ initial = 0, current = 0, target = 0 }) => {
  const max = 25;
  return (
    <div className="space-y-2" data-testid="risk-level-indicator">
      {[
        { label: 'Initieel', value: initial, color: 'bg-neutral-400' },
        { label: 'Huidig', value: current, color: 'bg-amber-500' },
        { label: 'Target', value: target, color: 'bg-emerald-500' },
      ].map(item => (
        <div key={item.label} className="flex items-center gap-3">
          <span className="text-[10px] text-neutral-500 w-12 shrink-0">{item.label}</span>
          <div className="flex-1 h-2.5 bg-neutral-100 rounded-full overflow-hidden">
            <div className={`h-full ${item.color} rounded-full transition-all duration-500`}
              style={{ width: `${Math.max(4, (item.value / max) * 100)}%` }} />
          </div>
          <span className="text-[10px] font-semibold text-neutral-600 w-6 text-right">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

export const ControlCard = ({ control, onEdit }) => {
  const statusColors = {
    'Gedefinieerd': 'bg-blue-100 text-blue-700',
    'Midden': 'bg-amber-100 text-amber-700',
    'Voldoende': 'bg-green-100 text-green-700',
    'Hoog': 'bg-emerald-100 text-emerald-800',
  };
  return (
    <div className="p-3 border border-neutral-200 rounded-lg bg-white" data-testid={`control-card-${control.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-black truncate">{control.title}</p>
          {control.description && <p className="text-[10px] text-neutral-500 mt-0.5 line-clamp-2">{control.description}</p>}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onEdit(control); }} className="shrink-0 p-1 hover:bg-neutral-100 rounded" data-testid={`edit-control-${control.id}`}>
          <Pencil className="h-3 w-3 text-neutral-400" strokeWidth={1.5} />
        </button>
      </div>
      <Badge className={`${statusColors[control.status] || 'bg-neutral-100 text-neutral-600'} text-[9px] mt-2`}>
        {control.status || 'Onbekend'}
      </Badge>
    </div>
  );
};

export const ActionCard = ({ action, onEdit }) => {
  const statusColors = {
    'Open': 'bg-blue-100 text-blue-700',
    'Gestart': 'bg-amber-100 text-amber-700',
    'Afgerond': 'bg-emerald-100 text-emerald-800',
  };
  const priorityColors = {
    'Laag': 'bg-green-100 text-green-700',
    'Midden': 'bg-amber-100 text-amber-700',
    'Hoog': 'bg-red-100 text-red-700',
  };
  return (
    <div className="p-3 border border-neutral-200 rounded-lg bg-white" data-testid={`action-card-${action.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-black truncate">{action.title}</p>
          {action.owner && <p className="text-[10px] text-neutral-500 mt-0.5">{action.owner}</p>}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onEdit(action); }} className="shrink-0 p-1 hover:bg-neutral-100 rounded" data-testid={`edit-action-${action.id}`}>
          <Pencil className="h-3 w-3 text-neutral-400" strokeWidth={1.5} />
        </button>
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <Badge className={`${statusColors[action.status] || 'bg-neutral-100 text-neutral-600'} text-[9px]`}>
          {action.status || 'Open'}
        </Badge>
        {action.priority && (
          <Badge className={`${priorityColors[action.priority] || 'bg-neutral-100 text-neutral-600'} text-[9px]`}>
            {action.priority}
          </Badge>
        )}
      </div>
      {action.date && <p className="text-[10px] text-neutral-400 mt-1.5">{action.date}</p>}
    </div>
  );
};

export const RiskHeatmap = ({ risks, onCellClick, activeFilter }) => {
  const impactLabels = ['Verwaarloosbaar', 'Beperkt', 'Aanzienlijk', 'Ernstig', 'Catastrofaal'];

  const matrix = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []));
  risks.forEach(r => {
    const li = Math.min(5, Math.max(1, r.probability || 3)) - 1;
    const im = Math.min(5, Math.max(1, r.impact || 3)) - 1;
    matrix[li][im].push(r);
  });

  return (
    <div className="w-full" data-testid="risk-heatmap">
      <div className="flex">
        <div className="flex flex-col items-center justify-center mr-2 w-5">
          <span className="text-[9px] font-bold text-neutral-500 tracking-widest uppercase whitespace-nowrap" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Kans</span>
        </div>
        <div className="flex flex-col justify-between mr-2 py-1">
          {[5, 4, 3, 2, 1].map(n => (
            <div key={n} className="flex items-center justify-center h-0 flex-1">
              <span className="text-xs font-semibold text-neutral-400">{n}</span>
            </div>
          ))}
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-5 gap-2">
            {[4, 3, 2, 1, 0].map(li => (
              [0, 1, 2, 3, 4].map(im => {
                const score = (li + 1) * (im + 1);
                const cellRisks = matrix[li][im];
                const count = cellRisks.length;
                const isActive = activeFilter && activeFilter.likelihood === li + 1 && activeFilter.impact === im + 1;
                return (
                  <TooltipProvider key={`${li}-${im}`} delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className={`rounded-xl flex flex-col items-center justify-center transition-all border-2 h-14 ${
                            isActive ? 'border-black ring-2 ring-black/20 scale-105' : 'border-transparent'
                          } ${getHeatmapBg(score)} hover:scale-105 hover:shadow-md`}
                          style={{ color: getHeatmapColor(score) }}
                          onClick={() => onCellClick(li + 1, im + 1)}
                          data-testid={`heatmap-cell-${li + 1}-${im + 1}`}
                        >
                          {count > 0 ? (
                            <span className="font-black text-2xl leading-none">{count}</span>
                          ) : (
                            <span className="font-bold text-base opacity-40">{score}</span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs max-w-52">
                        <p className="font-semibold">Score: {score}, Kans {li + 1} x Impact {im + 1}</p>
                        {count > 0 ? (
                          <div className="mt-1 space-y-0.5">
                            <p className="text-neutral-400">{count} risico{count !== 1 ? "'s" : ''}:</p>
                            {cellRisks.map((r) => (
                              <p key={r.id || r.title} className="text-[10px] truncate">{r.title}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-neutral-400 mt-0.5">Geen risico's</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })
            )).flat()}
          </div>
          <div className="grid grid-cols-5 gap-2 mt-2">
            {[1, 2, 3, 4, 5].map(n => (
              <span key={n} className="text-xs font-semibold text-neutral-400 text-center">{n}</span>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-2 mt-0.5">
            {impactLabels.map((label) => (
              <span key={label} className="text-[8px] text-neutral-400 text-center truncate">{label}</span>
            ))}
          </div>
          <p className="text-[9px] font-bold text-neutral-500 text-center mt-2 uppercase tracking-widest">Impact</p>
        </div>
      </div>
    </div>
  );
};
